import { Suspense, lazy } from 'react';
import { Navigate, Outlet, RouteObject, useRoutes } from 'react-router-dom';
import { Spin } from 'antd';
import { useUserStore } from '@/stores/user';
import BasicLayout from '@/layouts/BasicLayout';

// 懒加载页面
const Login = lazy(() => import('@/pages/Login'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const User = lazy(() => import('@/pages/User'));
const Teacher = lazy(() => import('@/pages/Teacher'));
const Course = lazy(() => import('@/pages/Course'));
const Booking = lazy(() => import('@/pages/Booking'));
const Order = lazy(() => import('@/pages/Order'));
const Product = lazy(() => import('@/pages/Product'));
const Event = lazy(() => import('@/pages/Event'));
const AdminUser = lazy(() => import('@/pages/AdminUser'));
const Role = lazy(() => import('@/pages/Role'));
const Log = lazy(() => import('@/pages/Log'));

const PageLoading = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      gap: 16,
      background: '#f8fafc'
    }}
  >
    <Spin size="large" />
    <span style={{ color: '#64748b', fontSize: 14 }}>页面加载中...</span>
  </div>
);

// 登录拦截
const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const { isLogin } = useUserStore();
  return isLogin ? <>{children}</> : <Navigate to="/login" replace />;
};

// 权限拦截（简化版：无权限则跳转 403 或首页）
const PermissionGuard = ({ permission, children }: { permission: string; children: React.ReactNode }) => {
  const { hasPermission } = useUserStore();
  return hasPermission(permission) ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

const routes: RouteObject[] = [
  {
    path: '/login',
    element: (
      <Suspense fallback={<PageLoading />}>
        <Login />
      </Suspense>
    )
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <BasicLayout>
          <Suspense fallback={<PageLoading />}>
            <Outlet />
          </Suspense>
        </BasicLayout>
      </AuthGuard>
    ),
    children: [
      { path: '/', element: <Navigate to="/dashboard" replace /> },
      { path: '/dashboard', element: <Dashboard /> },
      {
        path: '/users',
        element: (
          <PermissionGuard permission="user:read">
            <User />
          </PermissionGuard>
        )
      },
      {
        path: '/teachers',
        element: (
          <PermissionGuard permission="teacher:read">
            <Teacher />
          </PermissionGuard>
        )
      },
      {
        path: '/courses',
        element: (
          <PermissionGuard permission="course:read">
            <Course />
          </PermissionGuard>
        )
      },
      {
        path: '/bookings',
        element: (
          <PermissionGuard permission="booking:read">
            <Booking />
          </PermissionGuard>
        )
      },
      {
        path: '/orders',
        element: (
          <PermissionGuard permission="order:read">
            <Order />
          </PermissionGuard>
        )
      },
      {
        path: '/products',
        element: (
          <PermissionGuard permission="product:read">
            <Product />
          </PermissionGuard>
        )
      },
      {
        path: '/events',
        element: (
          <PermissionGuard permission="event:read">
            <Event />
          </PermissionGuard>
        )
      },
      {
        path: '/system/admin-users',
        element: (
          <PermissionGuard permission="admin_user:read">
            <AdminUser />
          </PermissionGuard>
        )
      },
      {
        path: '/system/roles',
        element: (
          <PermissionGuard permission="role:read">
            <Role />
          </PermissionGuard>
        )
      },
      {
        path: '/system/logs',
        element: (
          <PermissionGuard permission="log:read">
            <Log />
          </PermissionGuard>
        )
      },
      { path: '*', element: <Navigate to="/dashboard" replace /> }
    ]
  }
];

export const AppRoutes = () => useRoutes(routes);

export default AppRoutes;
